import { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { UploadCloud, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { API } from '../App';

const enconOrange = '#ee7203';

/**
 * Drag-and-drop (+ browse) upload modal for Settings > Pricing. Accepts the
 * "Master Price List" style spreadsheet (Description / UOM / Best Price /
 * Supplier / Category / Supplier Product Code / Confirmed M4 Product Code /
 * ... columns) and upserts suppliers, categories & products — safe to re-run
 * whenever a fresh price list needs to be merged in (matches by Confirmed M4
 * code first, then supplier product code, then description; only logs price
 * history when the price actually changed).
 */
const PricingUploadModal = ({ isOpen, onClose, onUploaded, scope = 'main' }) => {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const reset = () => { setResult(null); setUploading(false); setDragOver(false); };
  const handleClose = () => { reset(); onClose(); };

  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('scope', scope);
      const res = await api.post(`${API}/pricing/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResult({ ok: true, ...res.data, filename: file.name });
      toast.success(res.data?.message || 'Price sheet imported successfully');
      onUploaded && onUploaded(res.data);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Upload failed — check the sheet has Description/UOM/Best Price/Supplier/Category columns';
      setResult({ ok: false, message: detail, filename: file.name });
      toast.error(detail);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) doUpload(file);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-white max-w-lg" data-testid="pricing-upload-modal">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2" style={{ color: enconOrange }}>
            <UploadCloud className="w-6 h-6" />
            Upload Price Sheet{scope === 'b29' ? ' — B29' : ''}
          </DialogTitle>
          <DialogDescription>
            Drop the supplier price list spreadsheet (Master Price List format) — new products are
            added, existing ones are updated (price changes are logged automatically).
          </DialogDescription>
        </DialogHeader>

        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => !uploading && fileInputRef.current?.click()}
          data-testid="pricing-upload-dropzone"
          className="mt-2 flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-xl cursor-pointer transition-colors"
          style={{
            borderColor: dragOver ? enconOrange : '#d1d5db',
            background: dragOver ? `${enconOrange}0d` : '#fafafa',
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={(e) => { doUpload(e.target.files?.[0]); e.target.value = ''; }}
            data-testid="pricing-upload-file-input"
          />
          {uploading ? (
            <>
              <Loader2 className="w-9 h-9 mb-2 animate-spin" style={{ color: enconOrange }} />
              <p className="text-sm font-medium text-gray-500">Importing…</p>
            </>
          ) : (
            <>
              <UploadCloud className="w-9 h-9 mb-2" style={{ color: enconOrange }} />
              <p className="text-sm text-gray-600"><span className="font-semibold">Click to browse</span> or drag &amp; drop</p>
              <p className="text-xs text-gray-400 mt-1">.xlsx price list</p>
            </>
          )}
        </div>

        {result && (
          <div
            data-testid="pricing-upload-result"
            className={`flex items-start gap-3 p-3 rounded-xl border text-sm ${result.ok ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}
          >
            {result.ok ? (
              <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            )}
            <p className={result.ok ? 'text-green-700' : 'text-red-600'}>{result.message}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PricingUploadModal;
